import { X } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { PersonPicker } from "@/components/person-picker";
import { Input } from "@/components/ui/input";
import {
  type RoadmapBlockerKind,
  type RoadmapItem,
  type RoadmapOverview,
  useAddRoadmapBlocker,
} from "@/lib/api";
import { inlineFailure } from "@/lib/api-errors";
import { formatNumber } from "@/lib/format";
import { byEmail, normalEmail, personLabel } from "@/lib/people";
import { cn } from "@/lib/utils";

import { ItemPicker } from "./item-picker";
import { StepButtons } from "./step-buttons";
import {
  ASK_MAX,
  blockerCreate,
  type BlockerDraft,
  blockerFailureMessage,
  EMPTY_BLOCKER_DRAFT,
  PARTY_MAX,
  waitCandidates,
} from "./utils";

/** Whom or what a blockage can name, in the order the form offers them. */
const KINDS: readonly RoadmapBlockerKind[] = ["people", "item", "party"];

interface AddBlockerFormProps {
  item: RoadmapItem;
  /** Every item of the roadmap and the dashboard's accounts, for whom or what it can name. */
  overview: Pick<RoadmapOverview, "items" | "people">;
  /** Closes the form: after the blockage was added, or on cancel. */
  onClose: () => void;
}

/**
 * A new blockage of an item, in its details: who it waits on, then what they
 * need to do. Who is people from the dashboard's accounts (Personen), another
 * item (one that does not wait on this one, `waitCandidates`) or anything
 * else in free text (Anders), each with its own themed control. What is
 * required for people and Anders, optional for an item, and takes at most
 * `ASK_MAX` characters, counted under it. Toevoegen stays off until the
 * server would take it (`blockerCreate`).
 */
export function AddBlockerForm({ item, overview, onClose }: AddBlockerFormProps) {
  const { t } = useTranslation();
  const ids = useId();
  const add = useAddRoadmapBlocker();
  const [draft, setDraft] = useState<BlockerDraft>(EMPTY_BLOCKER_DRAFT);
  // The failed attempt, worded at render so a language switch re-words it.
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);
  const blocker = blockerCreate(draft);
  const known = byEmail(overview.people);

  const submit = async () => {
    if (!blocker || add.isPending) {
      return;
    }
    setFailure(null);
    try {
      await add.mutateAsync({ itemId: item.id, blocker });
      onClose();
    } catch (error) {
      setFailure(inlineFailure({ error }));
    }
  };

  const pickPerson = (email: string, on: boolean) =>
    setDraft((current) => {
      const picked = normalEmail(email);
      const people = current.people.filter((other) => other !== picked);
      return { ...current, people: on ? [...people, picked] : people };
    });

  return (
    <form
      className="bg-accent-light/40 space-y-3 rounded-lg border p-3"
      aria-label={t("roadmap.blockers.addTitle")}
      data-section="add-blocker"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <div className="space-y-1.5">
        <p id={`${ids}-who`} className="text-foreground text-sm font-medium">
          {t("roadmap.blockers.who")}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <div
            role="group"
            aria-labelledby={`${ids}-who`}
            className="border-input bg-card inline-flex rounded-md border p-0.5"
          >
            {KINDS.map((kind) => (
              <button
                key={kind}
                type="button"
                aria-pressed={draft.kind === kind}
                // The form opens on its first choice, so the keyboard starts there.
                autoFocus={kind === KINDS[0]}
                onClick={() => setDraft((current) => ({ ...current, kind }))}
                className={cn(
                  "focus-visible:ring-ring ring-offset-background h-8 rounded-sm px-2.5 text-sm font-medium whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:outline-none pointer-coarse:h-9",
                  draft.kind === kind
                    ? "bg-primary text-primary-foreground"
                    : "text-foreground hover:bg-accent-light hover:text-primary",
                )}
              >
                {t(`roadmap.blockers.kinds.${kind}`)}
              </button>
            ))}
          </div>
          {draft.kind === "people" && (
            <PersonPicker
              people={overview.people}
              label={t("roadmap.blockers.choosePeople")}
              multiple
              chosen={draft.people}
              onPick={pickPerson}
              className="w-full sm:w-56"
            />
          )}
          {draft.kind === "item" && (
            <ItemPicker
              items={waitCandidates(item.id, overview.items)}
              value={draft.itemId}
              onChange={(itemId) => setDraft((current) => ({ ...current, itemId }))}
              label={t("roadmap.blockers.chooseItem")}
              className="w-full sm:w-64"
            />
          )}
          {draft.kind === "party" && (
            <Input
              aria-label={t("roadmap.blockers.other")}
              placeholder={t("roadmap.blockers.otherPlaceholder")}
              value={draft.party}
              maxLength={PARTY_MAX}
              onChange={(event) => setDraft({ ...draft, party: event.target.value })}
              className="h-9 w-full sm:w-64 pointer-coarse:h-10"
            />
          )}
        </div>
        {draft.kind === "people" && draft.people.length > 0 && (
          <ul className="flex flex-wrap gap-1.5 pt-1 pointer-coarse:gap-y-6">
            {draft.people.map((email) => {
              const name = personLabel(known.get(email) ?? { email });
              return (
                <li
                  key={email}
                  className="bg-accent-light text-primary inline-flex max-w-full items-center gap-1 rounded-sm py-0.5 pr-0.5 pl-2 text-xs font-medium"
                >
                  <span className="truncate">{name}</span>
                  <button
                    type="button"
                    onClick={() => pickPerson(email, false)}
                    aria-label={t("roadmap.form.remove", { name })}
                    className="hover:bg-primary hover:text-primary-foreground focus-visible:ring-ring tap-target rounded-sm p-0.5 focus-visible:ring-2 focus-visible:outline-none pointer-coarse:relative"
                  >
                    <X aria-hidden="true" className="size-3" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <div className="space-y-1.5">
        <label htmlFor={`${ids}-ask`} className="text-foreground block text-sm font-medium">
          {t(draft.kind === "item" ? "roadmap.blockers.askOptional" : "roadmap.blockers.ask")}
        </label>
        <Input
          id={`${ids}-ask`}
          value={draft.ask}
          maxLength={ASK_MAX}
          // The ask is written in the item's language, as its title and summary are.
          lang={item.language}
          onChange={(event) => setDraft({ ...draft, ask: event.target.value })}
          aria-describedby={`${ids}-ask-hint ${ids}-ask-count`}
          className="h-9 pointer-coarse:h-10"
        />
        <div className="flex items-start justify-between gap-3 text-xs">
          <p id={`${ids}-ask-hint`} className="text-muted min-w-0">
            {t("roadmap.blockers.askHint")}
          </p>
          <AskCount id={`${ids}-ask-count`} length={draft.ask.length} />
        </div>
      </div>
      {failure && (
        <p role="alert" className="text-destructive text-sm">
          {blockerFailureMessage(failure.error)}
        </p>
      )}
      <StepButtons
        onBack={onClose}
        confirm={t("roadmap.blockers.create")}
        pending={add.isPending}
        disabled={!blocker}
      />
    </form>
  );
}

interface AskCountProps {
  id: string;
  /** The characters typed so far. */
  length: number;
}

/**
 * How much of "Wat" is used: "12/80" on screen, "12 van 80 tekens" for a
 * screen reader, which reads it with the field. In the main blue once full.
 */
function AskCount({ id, length }: AskCountProps) {
  const { t } = useTranslation();
  const values = { length: formatNumber(length), max: formatNumber(ASK_MAX) };
  return (
    <p
      id={id}
      className={cn(
        "shrink-0 tabular-nums",
        length >= ASK_MAX ? "text-primary font-medium" : "text-muted",
      )}
      data-ask-count=""
    >
      <span aria-hidden="true">{t("roadmap.blockers.askCount", values)}</span>
      <span className="sr-only">{t("roadmap.blockers.askCountLabel", values)}</span>
    </p>
  );
}
